import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { absoluteUrl, SITE_NAME } from './siteConfig';
import { getRouteSeo } from './routeSeo';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Single centralized route-aware head manager.
 * On every route change it replaces all managed tags so metadata from the
 * previous route cannot leak into the next one.
 */
export default function SeoHead() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getRouteSeo(pathname);
    const canonical = seo.indexable ? absoluteUrl(seo.path) : '';

    document.title = seo.title;

    upsertMeta('name', 'description', seo.description);
    if (seo.keywords) {
      upsertMeta('name', 'keywords', seo.keywords);
    }
    upsertMeta('name', 'robots', seo.indexable ? 'index, follow' : 'noindex, nofollow');

    if (canonical) {
      upsertCanonical(canonical);
    }

    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:type', seo.ogType ?? 'website');
    upsertMeta('property', 'og:site_name', SITE_NAME);
    if (canonical) {
      upsertMeta('property', 'og:url', canonical);
    }

    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);
  }, [pathname]);

  return null;
}